import type { OfflineDatabaseSqlStatement } from './offlineDatabase.statements';

export const buildTemplatesCacheDeleteByIdsStatement = (
  templateIds: number[]
): OfflineDatabaseSqlStatement => {
  if (templateIds.length === 0) {
    return {
      sql: 'DELETE FROM templates_cache WHERE 1=0',
      params: [],
    };
  }

  return {
    sql: `DELETE FROM templates_cache WHERE id IN (${templateIds.map(() => '?').join(', ')})`,
    params: templateIds,
  };
};

export const buildTemplateCacheInsertStatement = (
  templateId: number,
  data: string,
  expiresAt: string
): OfflineDatabaseSqlStatement => ({
  sql: 'INSERT INTO templates_cache (id, data, expires_at) VALUES (?, ?, ?)',
  params: [templateId, data, expiresAt],
});

export const buildValidTemplatesCacheSelectStatement = (
  now: string
): OfflineDatabaseSqlStatement => ({
  sql: 'SELECT * FROM templates_cache WHERE expires_at > ? ORDER BY id',
  params: [now],
});

export const buildTemplatesCacheFallbackSelectStatement = (): OfflineDatabaseSqlStatement => ({
  sql: 'SELECT * FROM templates_cache ORDER BY expires_at DESC, id',
  params: [],
});

export const buildCategoriesCacheClearStatement = (): OfflineDatabaseSqlStatement => ({
  sql: 'DELETE FROM categories_cache',
  params: [],
});

export const buildCategoryCacheInsertStatement = (
  categoryId: number,
  templateId: number,
  data: string,
  expiresAt: string
): OfflineDatabaseSqlStatement => ({
  sql: 'INSERT INTO categories_cache (id, template_id, data, expires_at) VALUES (?, ?, ?, ?)',
  params: [categoryId, templateId, data, expiresAt],
});

export const buildCategoriesCacheSelectStatement = (
  now: string,
  templateId?: number
): OfflineDatabaseSqlStatement => {
  let sql = 'SELECT * FROM categories_cache WHERE expires_at > ?';
  const params: unknown[] = [now];

  if (templateId !== undefined) {
    sql += ' AND template_id = ?';
    params.push(templateId);
  }

  sql += ' ORDER BY template_id, id';

  return { sql, params };
};

export const buildItemsCacheClearStatement = (): OfflineDatabaseSqlStatement => ({
  sql: 'DELETE FROM items_cache',
  params: [],
});

export const buildItemCacheInsertStatement = (
  itemId: number,
  categoryId: number,
  data: string,
  expiresAt: string
): OfflineDatabaseSqlStatement => ({
  sql: 'INSERT INTO items_cache (id, category_id, data, expires_at) VALUES (?, ?, ?, ?)',
  params: [itemId, categoryId, data, expiresAt],
});

export const buildItemsCacheSelectStatement = (
  now: string,
  categoryId?: number
): OfflineDatabaseSqlStatement => {
  let sql = 'SELECT * FROM items_cache WHERE expires_at > ?';
  const params: unknown[] = [now];

  if (categoryId !== undefined) {
    sql += ' AND category_id = ?';
    params.push(categoryId);
  }

  sql += ' ORDER BY category_id, id';

  return { sql, params };
};

export const buildRoomsCacheClearForPropertyStatement = (
  propertyId: number
): OfflineDatabaseSqlStatement => ({
  sql: 'DELETE FROM rooms_cache WHERE property_id = ?',
  params: [propertyId],
});

export const buildRoomsCacheClearDefaultStatement = (): OfflineDatabaseSqlStatement => ({
  sql: 'DELETE FROM rooms_cache WHERE property_id IS NULL',
  params: [],
});

export const buildRoomCacheInsertStatement = (
  roomId: number,
  propertyId: number | null,
  data: string,
  expiresAt: string
): OfflineDatabaseSqlStatement => ({
  sql: 'INSERT INTO rooms_cache (id, property_id, data, expires_at) VALUES (?, ?, ?, ?)',
  params: [roomId, propertyId, data, expiresAt],
});

export const buildRoomsCacheSelectStatement = (
  now: string,
  propertyId?: number
): OfflineDatabaseSqlStatement => {
  let sql = 'SELECT * FROM rooms_cache WHERE expires_at > ?';
  const params: unknown[] = [now];

  if (propertyId !== undefined) {
    sql += ' AND property_id = ?';
    params.push(propertyId);
  } else {
    sql += ' AND (property_id IS NULL OR property_id IN (?, ?))';
    params.push('default', 'defaults');
  }

  sql += ' ORDER BY property_id, id';

  return { sql, params };
};
